import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import { FilePdf, MicrosoftExcelLogo } from "@phosphor-icons/react";
import MonthPicker from "@/components/MonthPicker";

const TYPES = [
  { id: "All", label: "All" },
  { id: "PREPAYMENT", label: "Prepayments" },
  { id: "ACCRUAL", label: "Accruals" },
  { id: "DEFERRED_INCOME", label: "Deferred Income" },
];
const CAT_LABEL = { PREPAYMENT: "Prepayments", ACCRUAL: "Accruals", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const CAT_ORDER = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME", "DEPRECIATION"];

const lastDayOfMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Movement() {
  const [params] = useSearchParams();
  const [type, setType] = useState(TYPES.some((t) => t.id === params.get("type")) ? params.get("type") : "All");
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(thisMonth());
  const [start, setStart] = useState(`${thisMonth()}-01`);
  const [end, setEnd] = useState(`${thisMonth()}-${String(lastDayOfMonth(thisMonth())).padStart(2, "0")}`);
  const [basis, setBasis] = useState("schedule");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const range = useCallback(() => {
    if (mode === "month") {
      return { start: `${month}-01`, end: `${month}-${String(lastDayOfMonth(month)).padStart(2, "0")}` };
    }
    return { start, end };
  }, [mode, month, start, end]);

  const load = useCallback(async () => {
    setLoading(true);
    const { start: s, end: e } = range();
    try {
      const { data } = await api.get("/reports/movement", { params: { start: s, end: e, type, basis } });
      setData(data);
    } catch {
      toast.error("Could not load the movement report");
    } finally { setLoading(false); }
  }, [range, type, basis]);

  useEffect(() => { load(); }, [load]);

  const download = async (ext) => {
    setBusy(ext);
    const { start: s, end: e } = range();
    const path = `/reports/movement/${ext === "pdf" ? "pdf" : "excel"}`;
    try {
      const res = await api.get(path, { params: { start: s, end: e, type, basis }, responseType: "blob" });
      const mime = ext === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `movement-${s}.${ext === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the file");
    } finally { setBusy(""); }
  };

  const groups = CAT_ORDER
    .map((cat) => ({ cat, rows: (data?.rows || []).filter((r) => r.category === cat), sub: data?.by_category?.[cat] }))
    .filter((g) => g.rows.length > 0);
  const t = data?.total || { opening: 0, movement: 0, closing: 0 };

  return (
    <Page>
      <PageHeader overline="Reporting" title="Movement Report"
        subtitle="Opening balance, movement in the period and closing balance — per line item, grouped by type." />

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-4 mb-5 flex flex-wrap items-end gap-4" data-testid="movement-controls">
        {/* Type */}
        <div>
          <div className="overline text-navy/65 mb-1.5">Type</div>
          <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1" data-testid="movement-type-filter">
            {TYPES.map((tp) => (
              <button key={tp.id} onClick={() => setType(tp.id)} data-testid={`movement-type-${tp.id}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${type === tp.id ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>{tp.label}</button>
            ))}
          </div>
        </div>

        {/* Period */}
        <div>
          <div className="overline text-navy/65 mb-1.5">Period</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
              <button onClick={() => setMode("month")} data-testid="movement-mode-month"
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "month" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>Month</button>
              <button onClick={() => setMode("custom")} data-testid="movement-mode-custom"
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "custom" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>Custom</button>
            </div>
            {mode === "month" ? (
              <MonthPicker value={month} onChange={setMonth} yearsBack={6} yearsForward={2} testId="movement-month" />
            ) : (
              <div className="flex items-center gap-2">
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="movement-start-input"
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
                <span className="text-navy/65 text-sm">to</span>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="movement-end-input"
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
              </div>
            )}
          </div>
        </div>

        {/* Basis */}
        <div>
          <div className="overline text-navy/65 mb-1.5">Basis</div>
          <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1" data-testid="movement-basis-filter">
            <button onClick={() => setBasis("schedule")} data-testid="movement-basis-schedule"
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${basis === "schedule" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}
              title="Follows the planned release schedule (reconciles to how journals post)">Release schedule</button>
            <button onClick={() => setBasis("posted")} data-testid="movement-basis-posted"
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${basis === "posted" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}
              title="Counts only journals actually posted to Xero">Posted to date</button>
          </div>
        </div>

        {/* Exports */}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => download("pdf")} disabled={busy === "pdf"} data-testid="movement-pdf-btn"
            className="flex items-center gap-2 border border-slate-200 text-navy px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors disabled:opacity-60">
            <FilePdf size={16} /> {busy === "pdf" ? "…" : "PDF"}
          </button>
          <button onClick={() => download("excel")} disabled={busy === "excel"} data-testid="movement-excel-btn"
            className="flex items-center gap-2 border border-positive/40 text-positive px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors disabled:opacity-60">
            <MicrosoftExcelLogo size={16} /> {busy === "excel" ? "…" : "Excel"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-navy/65 text-sm" data-testid="movement-loading">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-16 text-center text-navy/70" data-testid="movement-empty">
          <div className="font-heading font-bold text-lg text-navy">No movement in this period</div>
          <p className="text-sm mt-1">No opening balance or movement for {data?.period_label} on this basis. Try another month or type.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-auto max-h-[70vh]" data-testid="movement-table">
          <table className="w-full text-sm" style={{ minWidth: 1000 }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <Th>Reference</Th><Th>Description</Th><Th>BS Code</Th><Th>BS Account</Th>
                <Th>P&L Code</Th><Th>P&L Account</Th>
                <Th right>Opening</Th><Th right>Movement</Th><Th right>Closing</Th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.cat}>
                  <tr className="bg-navy text-white">
                    <td colSpan={9} className="px-4 py-2 font-heading font-bold text-sm">{CAT_LABEL[g.cat]} ({g.rows.length})</td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.schedule_id} data-testid={`movement-row-${r.schedule_id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <Td mono>{r.reference}</Td>
                      <Td>{r.description}<div className="text-xs text-slate-600">{r.contact}</div></Td>
                      <Td mono>{r.source_account_code}</Td>
                      <Td>{r.source_account_name}</Td>
                      <Td mono>{r.pl_account_code}</Td>
                      <Td>{r.pl_account_name}</Td>
                      <Td right mono>{money(r.opening, r.currency)}</Td>
                      <Td right mono className="text-orangeink">{money(r.movement, r.currency)}</Td>
                      <Td right mono className="font-semibold text-navy">{money(r.closing, r.currency)}</Td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 border-b border-slate-200" data-testid={`movement-subtotal-${g.cat}`}>
                    <Td className="font-semibold text-navy">Subtotal · {CAT_LABEL[g.cat]}</Td>
                    <Td /><Td /><Td /><Td /><Td />
                    <Td right mono className="font-semibold">{money(g.sub?.opening, data.currency)}</Td>
                    <Td right mono className="font-semibold">{money(g.sub?.movement, data.currency)}</Td>
                    <Td right mono className="font-semibold">{money(g.sub?.closing, data.currency)}</Td>
                  </tr>
                </Fragment>
              ))}
              <tr className="bg-orange/10 border-t-2 border-orange" data-testid="movement-grand-total">
                <Td className="font-heading font-bold text-navy">GRAND TOTAL</Td>
                <Td /><Td /><Td /><Td /><Td />
                <Td right mono className="font-bold text-navy">{money(t.opening, data.currency)}</Td>
                <Td right mono className="font-bold text-navy">{money(t.movement, data.currency)}</Td>
                <Td right mono className="font-bold text-navy">{money(t.closing, data.currency)}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-navy/65 mt-3">
        Opening = balance at the start of the period · Movement = releases during the period · Closing = Opening − Movement.
        {basis === "posted" ? " Basis: only journals posted to Xero." : " Basis: planned release schedule (reconciles to how journals post)."}
      </p>
    </Page>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 text-xs font-semibold text-navy/70 uppercase tracking-wide bg-slate-50 ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, mono, className = "" }) => (
  <td className={`px-4 py-3 text-navy/80 ${right ? "text-right" : ""} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>
);
